import React, { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { DormantIcon } from '@/src/renderer/components/icons/DormantIcon'
import type { Task } from '@/src/renderer/lib/db/models'
import { SelectionActions, type SelectionMove, type SelectionNest } from '../components/ListToolbar/SelectionActions'
import { taskMenuHandlers } from '../components/TaskMenu/taskMenuHandlers'
import { wakeTaskUpdate } from '../utils/buildTaskUpdate'
import { removableMarkers } from '../utils/removableMarkers'
import type { MoveMenu } from '../utils/moveMenu'
import type { NestMenu } from '../utils/nestMenu'
import {
  SELECTION_MIN_TASKS,
  layoutSelectionCommands,
  resolveSelectionCommand,
  type SelectionCommandId,
  type SelectionTask,
} from '../utils/selectionCommands'
import { taskToEntryData } from '../utils/taskToEntryData'
import type { TaskUpdates } from './useTaskUpdates'

export interface UseSelectionActionsParams {
  selectedIds: ReadonlySet<string>
  /** Die Aufgaben der Liste in ihrer Reihenfolge (`flatNavList` ohne Abschnittsköpfe). */
  orderedTaskIds: readonly string[]
  tasks: readonly Task[] | undefined
  /** Der Schreibweg des Aufgaben-Menüs auf genau diese Aufgaben (`useTaskUpdates`). */
  updates: TaskUpdates
  /** Schaltet jede der Aufgaben um — der Weg der Leertaste (`toggleCompleteWithGrace`). */
  toggleComplete: (ids: Set<string>) => void
  /** Löscht die Aufgaben, mit einem Rückgängig — der Weg von ⌫ (`handleDeleteTask`). */
  deleteTasks: (ids: Set<string>) => void
  duplicateTasks: (ids: string[]) => void
  clearSelection: () => void
  moveMenu: MoveMenu | null
  nestMenu: NestMenu | null
  /**
   * Nach jedem Schreiben aus der Pille, mit genau den Aufgaben, auf die es wirkte. Kurz notiert erkennt daran,
   * was die Liste verlässt (`noteUpdate`, F-KN3) — die Seite muss es sagen, auch wenn sie nichts braucht (`null`).
   */
  onWritten: ((ids: readonly string[], changes: Partial<Omit<Task, 'id'>>) => void) | null
  /** Danach gehört die Tastatur wieder der Liste (`restoreListFocus` der Navigation, wie #284 an der Zeile). */
  restoreListFocus: (() => void) | null
}

const asSelectionTask = (task: Task): SelectionTask => ({
  id: task.id,
  completed: task.status === 'completed',
  dormant: task.pausedAt != null,
  favorite: task.favoriteOrder != null,
})

/**
 * **Die Befehle der Auswahl in der Listen-Werkzeug-Pille** (#291) für eine Seite mit Aufgabenliste:
 * Projekt und Kurz notiert. Liefert `renderSelection` für den `ListToolbarDock` — oder `undefined`,
 * solange weniger als zwei Aufgaben markiert sind (eine einzelne ist nur der Cursor).
 *
 * Gezählt wird nur, was als Aufgabe in der Liste steht: keine Abschnittsköpfe (⌘A), nichts, was die
 * Liste nach dem Verschieben nicht mehr zeigt. Jeder Befehl schreibt nur auf die Aufgaben, für die er
 * gilt (`resolveSelectionCommand`) — Start etwa nicht auf erledigte.
 */
export function useSelectionActions({
  selectedIds, orderedTaskIds, tasks, updates, toggleComplete, deleteTasks, duplicateTasks, clearSelection, moveMenu, nestMenu,
  onWritten, restoreListFocus,
}: UseSelectionActionsParams): ((slots: number) => React.ReactNode) | undefined {
  const { t } = useTranslation()

  const selected = useMemo(() => {
    const byId = new Map((tasks ?? []).map(task => [task.id, task]))
    return orderedTaskIds.filter(id => selectedIds.has(id)).map(id => byId.get(id)).filter((task): task is Task => !!task)
  }, [tasks, orderedTaskIds, selectedIds])
  const selection = useMemo(() => selected.map(asSelectionTask), [selected])

  // Die Griffe lesen den Stand beim Klick, nicht beim Rendern — der Knopf lebt länger als ein Bild.
  const latest = useRef({ selected, selection, updates, toggleComplete, deleteTasks, duplicateTasks, clearSelection, onWritten, t })
  latest.current = { selected, selection, updates, toggleComplete, deleteTasks, duplicateTasks, clearSelection, onWritten, t }

  // Jeder Schreibweg der Pille meldet sich danach (`onWritten`) — sonst ginge Kurz notiert eine Tür verloren.
  const writeTo = useCallback((ids: readonly string[], changes: Partial<Omit<Task, 'id'>>) => {
    void latest.current.updates.applyTo(ids, changes)
    latest.current.onWritten?.(ids, changes)
  }, [])

  const run = useCallback((id: SelectionCommandId) => {
    const { selected: rows, selection: picked, updates: write, t: translate } = latest.current
    const command = resolveSelectionCommand(id, picked)
    const fits = command.fits
    if (fits.length === 0) return
    switch (id) {
      case 'complete':
        latest.current.toggleComplete(new Set(fits))
        break
      case 'rest':
        if (command.mode === 'rest') {
          writeTo(fits, { pausedAt: new Date() })
          showSuccessToast({
            message: translate('taskItem.pausedToast', { count: fits.length }),
            description: translate('taskItem.pausedToastText', { count: fits.length }),
            icon: <DormantIcon size={18} />,
            tone: 'rest',
          })
        } else {
          const now = new Date()
          const writes = rows.filter(row => fits.includes(row.id)).map(row => ({ id: row.id, changes: wakeTaskUpdate(row, now) }))
          void write.applyWrites(writes)
          for (const { id: woken, changes } of writes) latest.current.onWritten?.([woken], changes)
        }
        break
      case 'favorite':
        // Neu favorisiert landet unten in „Favoriten“ — wie im Aufgaben-Menü (`useContextMenuBadgeUpdate`).
        writeTo(fits, { favoriteOrder: command.mode === 'remove' ? null : Date.now() })
        break
      case 'duplicate':
        latest.current.duplicateTasks(fits)
        break
      case 'delete':
        latest.current.deleteTasks(new Set(fits))
        latest.current.clearSelection()
        break
    }
  }, [writeTo])

  const openIds = useMemo(() => selection.filter(task => !task.completed).map(task => task.id), [selection])
  const handlers = useMemo(
    () => taskMenuHandlers(changes => writeTo(openIds, changes)),
    [openIds, writeTo],
  )
  // Aus genau den Aufgaben, auf die die Kästen schreiben — Marken erledigter zählen nicht (#313).
  const removable = useMemo(() => removableMarkers(selected.filter(task => task.status !== 'completed')), [selected])

  const anchor = selected.find(task => task.status !== 'completed') ?? selected[0]
  const anchorId = anchor?.id ?? ''

  const move = useMemo<SelectionMove | undefined>(() => moveMenu ? {
    startSpace: anchor?.lifeSpace ?? 'personal',
    countMoving: () => moveMenu.countMoving(anchorId, openIds),
    onSelect: (target, place) => {
      moveMenu.move(anchorId, target, place, openIds)
      latest.current.clearSelection()
    },
    onCreateProject: (project) => {
      moveMenu.moveToNewProject(anchorId, project, openIds)
      latest.current.clearSelection()
    },
  } : undefined, [moveMenu, anchor?.lifeSpace, anchorId, openIds])

  const nest = useMemo<SelectionNest | undefined>(() => nestMenu ? {
    groups: () => nestMenu.groupsFor(anchorId, openIds),
    onSelect: (parentId) => nestMenu.nest(anchorId, parentId, openIds),
  } : undefined, [nestMenu, anchorId, openIds])

  const restMode = useMemo(() => resolveSelectionCommand('rest', selection), [selection])
  const onRest = restMode.mode === 'rest' && restMode.fits.length > 0 ? () => run('rest') : undefined

  if (selection.length < SELECTION_MIN_TASKS || !anchor) return undefined

  const unavailable: SelectionCommandId[] = [
    ...(moveMenu ? [] : ['move' as const]),
    ...(nestMenu ? [] : ['nest' as const]),
  ]
  const task = taskToEntryData(anchor)
  return (slots: number) => (
    <SelectionActions
      count={selection.length}
      layout={layoutSelectionCommands(selection, { slots, unavailable })}
      task={task}
      handlers={handlers}
      removable={removable}
      onCommand={run}
      move={move}
      nest={nest}
      onRest={onRest}
      onDone={restoreListFocus ?? undefined}
    />
  )
}
